import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EventSeatOutlinedIcon from "@mui/icons-material/EventSeatOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import { useAlert } from "../../../context/AlertContext";
import EmployeePopup from "./EmployeePopup";
import MaxBookingsPopup from "./MaxBookingsPopup";
import CopyWeekModal from "./CopyWeekModal";
import CalendarWeekNav from "../components/CalendarWeekNav";
import MobileHeader from "../components/MobileHeader";
import MobileWeekGrid from "../components/MobileWeekGrid";
import "./appointments.css";
import {
  HOUR_ROWS,
  DAYS_PER_WEEK,
  WEEK_DAY_LABELS,
  useIsDesktopTablet,
  cellKey,
} from "./termini-shared";

const MAX_EMPLOYEES_PER_TIMESLOT = 3;
// Matches the backend's own DEFAULT_MAX_BOOKINGS (timeslots.ts) — the value the "Promeni Broj
// Vežbača" popup opens with when the selected slots don't all already share the same
// max_bookings (so there's no single existing value to preselect).
const DEFAULT_MAX_BOOKINGS = 7;

// `backTo` (optional): when set, renders a "← Nazad" affordance and navigates there instead of
// relying on browser/gesture history — used now that Termini is reached via Settings
// (Settings → Termini → Nazad → Settings) rather than being a top-level nav destination.
// Absent for any other future caller, this renders exactly as before (no back UI at all).
const TimeslotsSection = ({ backTo }) => {
  const { showAlert } = useAlert();
  const isDesktopTablet = useIsDesktopTablet();
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeslotsByStart, setTimeslotsByStart] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [employeePopup, setEmployeePopup] = useState(null);
  const [maxBookingsPopup, setMaxBookingsPopup] = useState(null);
  const [copyWeekOpen, setCopyWeekOpen] = useState(false);

  const weekStart = useMemo(
    () => moment(selectedDate).clone().startOf("week"),
    [selectedDate]
  );

  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < DAYS_PER_WEEK; i++) {
      dates.push(moment(weekStart).add(i, "days").toDate());
    }
    return dates;
  }, [weekStart]);

  const fetchTimeslots = async () => {
    try {
      const startDate = weekStart.format("YYYY-MM-DD");
      const response = await ApiMethods.get(`timeslots?start=${startDate}`);
      const byStart = {};
      (response || []).forEach((ts) => {
        const key = moment.parseZone(ts.start).format("YYYY-MM-DD HH:mm:ss");
        byStart[key] = { ...ts, employees: ts.employees || [] };
      });
      setTimeslotsByStart(byStart);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchTimeslots();
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleNavigate = (direction) => {
    const amount = direction === "next" ? 1 : -1;
    setSelectedDate((prev) => moment(prev).add(amount, "week").toDate());
  };

  const goToToday = () => setSelectedDate(new Date());

  const toggleCell = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const selectedKeys = Array.from(selected);
  const selectedExisting = selectedKeys
    .map((key) => timeslotsByStart[key])
    .filter(Boolean);
  const selectedEmptyKeys = selectedKeys.filter((key) => !timeslotsByStart[key]);

  const canCreate = selectedEmptyKeys.length > 0;
  const canDelete = selectedExisting.length > 0;
  const canHide = selectedExisting.some((ts) => ts.status !== "hidden");
  const canShow = selectedExisting.some((ts) => ts.status === "hidden");
  const canEditEmployees = selectedExisting.length > 0;
  const canChangeMaxBookings = selectedExisting.length > 0;

  // Desktop/Tablet detail panel: human-readable rows for each selected
  // slot (day + time range), sorted chronologically. Pure display
  // formatting derived from `selected` — no new state, mirrors how
  // `weekLabel` below already formats existing data for display.
  const selectedSlotsSorted = useMemo(() => {
    return Array.from(selected)
      .sort()
      .map((key) => {
        const start = moment(key, "YYYY-MM-DD HH:mm:ss");
        return {
          key,
          dateLabel: start.format("dddd, D. MMM YYYY."),
          timeLabel: `${start.format("HH:mm")} – ${start
            .clone()
            .add(1, "hour")
            .format("HH:mm")}`,
        };
      });
  }, [selected]);

  const withSaving = async (fn) => {
    setSaving(true);
    try {
      await fn();
    } catch (err) {
      console.log(err);
      showAlert("Došlo je do greške", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = () => {
    if (!canCreate) return;
    withSaving(async () => {
      const timeslots = selectedEmptyKeys.map((key) => ({
        start: key,
        end: moment(key, "YYYY-MM-DD HH:mm:ss").add(1, "hour").format("YYYY-MM-DD HH:mm:ss"),
      }));
      await ApiMethods.post("timeslots/create", { timeslots });
      showAlert("Termini su kreirani", "success");
      clearSelection();
      await fetchTimeslots();
    });
  };

  const handleDelete = () => {
    if (!canDelete) return;
    withSaving(async () => {
      const ids = selectedExisting.map((ts) => ts.id);
      await ApiMethods.post("timeslots/delete", { ids });
      showAlert("Termini su obrisani", "success");
      clearSelection();
      await fetchTimeslots();
    });
  };

  const handleSetVisibility = (status) => {
    const relevant = selectedExisting.filter((ts) =>
      status === "hidden" ? ts.status !== "hidden" : ts.status === "hidden"
    );
    if (relevant.length === 0) return;
    withSaving(async () => {
      const ids = relevant.map((ts) => ts.id);
      await ApiMethods.post("timeslots/visibility", { ids, status });
      showAlert(
        status === "hidden" ? "Termini su sakriveni" : "Termini su prikazani",
        "success"
      );
      clearSelection();
      await fetchTimeslots();
    });
  };

  const openEmployeePopup = async () => {
    if (!canEditEmployees) return;
    try {
      const timeslot_ids = selectedExisting.map((ts) => ts.id);
      const [employees, assigned] = await Promise.all([
        ApiMethods.get("timeslots/employees"),
        ApiMethods.post("timeslots/employees/for-slots", { timeslot_ids }),
      ]);
      setEmployeePopup({
        employees: employees || [],
        assignedEmployeeIds: (assigned || []).map((e) => e.id),
      });
    } catch (err) {
      console.log(err);
      showAlert("Greška pri učitavanju zaposlenih", "error");
    }
  };

  const handleConfirmEmployeePopup = (employeeIds) => {
    if (!employeePopup) return;
    const timeslot_ids = selectedExisting.map((ts) => ts.id);
    const initialIds = new Set(employeePopup.assignedEmployeeIds);
    const nextIds = new Set(employeeIds);
    const toAdd = employeeIds.filter((id) => !initialIds.has(id));
    const toRemove = employeePopup.assignedEmployeeIds.filter(
      (id) => !nextIds.has(id)
    );

    if (toAdd.length === 0 && toRemove.length === 0) {
      setEmployeePopup(null);
      return;
    }

    withSaving(async () => {
      if (toAdd.length > 0) {
        await ApiMethods.post("timeslots/employees/add", {
          timeslot_ids,
          employee_ids: toAdd,
        });
      }
      if (toRemove.length > 0) {
        await ApiMethods.post("timeslots/employees/remove", {
          timeslot_ids,
          employee_ids: toRemove,
        });
      }
      showAlert("Zaposleni su sačuvani", "success");
      setEmployeePopup(null);
      clearSelection();
      await fetchTimeslots();
    });
  };

  // Preselects the shared max_bookings when every selected slot already has the same one,
  // otherwise falls back to the default ceiling (7) — there's no single "current" value to
  // show when the selection is a mix.
  const openMaxBookingsPopup = () => {
    if (!canChangeMaxBookings) return;
    const distinctValues = new Set(selectedExisting.map((ts) => ts.max_bookings));
    const initialValue =
      distinctValues.size === 1 ? [...distinctValues][0] : DEFAULT_MAX_BOOKINGS;
    setMaxBookingsPopup({ initialValue });
  };

  const handleConfirmMaxBookings = (maxBookings) => {
    if (!maxBookingsPopup) return;
    const ids = selectedExisting.map((ts) => ts.id);
    withSaving(async () => {
      await ApiMethods.post("timeslots/max-bookings", { ids, max_bookings: maxBookings });
      showAlert("Broj vežbača je izmenjen", "success");
      setMaxBookingsPopup(null);
      clearSelection();
      await fetchTimeslots();
    });
  };

  const employeeInitials = (employee) =>
    `${employee.first_name ? employee.first_name[0].toUpperCase() : ""}${
      employee.last_name ? employee.last_name[0].toUpperCase() : ""
    }`;

  const renderCell = (date, hour) => {
    const key = cellKey(date, hour);
    const timeslot = timeslotsByStart[key];
    const isSelected = selected.has(key);
    const isHidden = timeslot && timeslot.status === "hidden";
    const employees = (timeslot && timeslot.employees) || [];
    const visibleEmployees = employees.slice(0, 3);
    const overflowCount = employees.length - visibleEmployees.length;

    return (
      <div
        key={key}
        className={[
          "hf-tm-cell",
          timeslot ? "hf-tm-cell-filled" : "",
          isHidden ? "hf-tm-cell-hidden" : "",
          isSelected ? "hf-tm-cell-selected" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => toggleCell(key)}
      >
        <div className="hf-tm-cell-left">
          {isHidden && (
            <VisibilityOffOutlinedIcon className="hf-tm-cell-hidden-icon" />
          )}
          {employees.length > 0 && (
            <div className="hf-tm-cell-employees">
              {visibleEmployees.map((employee) => (
                <span key={employee.id} className="hf-tm-cell-employee-badge">
                  {employeeInitials(employee)}
                </span>
              ))}
              {overflowCount > 0 && (
                <span className="hf-tm-cell-employee-overflow">
                  +{overflowCount}
                </span>
              )}
            </div>
          )}
        </div>
        {/* The slot's own capacity ceiling (see "Promeni Broj Vežbača") — always shown for a
            created slot, not just when it differs from the default 7, so staff can see at a
            glance which slots have already been customized without opening each one. */}
        {timeslot && (
          <span className="hf-tm-cell-max-bookings">{timeslot.max_bookings}</span>
        )}
      </div>
    );
  };

  const weekLabel = `${weekDates[0].getDate()} ${moment(weekDates[0]).format(
    "MMM"
  )} - ${weekDates[DAYS_PER_WEEK - 1].getDate()} ${moment(
    weekDates[DAYS_PER_WEEK - 1]
  ).format("MMM")}`;

  // Compact "24.08. - 29.08.'26" form for the fixed Mobile header, between the hamburger and
  // bell — replaces the old plain "Termini" title there, same treatment as Calendar's own
  // MobileHeader (CalendarTest/index.js). `weekLabel` above stays as-is for Desktop/Tablet's
  // own in-page nav further down, untouched by this.
  const mobileWeekEnd = moment(weekStart).endOf("week");
  const mobileWeekLabel = `${weekStart.format(
    "DD.MM."
  )} - ${mobileWeekEnd.format("DD.MM.")}'${mobileWeekEnd.format("YY")}`;

  return (
    <div className="hf-tm-page">
      <MobileHeader
        title={
          <CalendarWeekNav
            variant="mobile"
            label={mobileWeekLabel}
            onPrev={() => handleNavigate("prev")}
            onNext={() => handleNavigate("next")}
            onToday={goToToday}
          />
        }
      />

      <div className="hf-tm-header">
        <div className="hf-tm-header-title-block">
          {backTo && (
            <button
              type="button"
              className="hf-tm-back-btn"
              onClick={() => navigate(backTo)}
            >
              <ArrowBackIcon fontSize="small" />
              Nazad
            </button>
          )}
          <h1 className="hf-tm-title">Termini</h1>
          <p className="hf-tm-subtitle">
            Upravljajte radnim vremenom i dostupnim terminima
          </p>
        </div>

        {/* Desktop/Tablet only now — Mobile's equivalent nav moved into the fixed MobileHeader
            above instead of duplicating it here (same change as Calendar's own
            CalendarTest/index.js). */}
        {isDesktopTablet && (
          <div className="hf-tm-week-nav">
            <CalendarWeekNav
              variant="desktop"
              label={weekLabel}
              onPrev={() => handleNavigate("prev")}
              onNext={() => handleNavigate("next")}
              onToday={goToToday}
            />
          </div>
        )}

        {/* Bulk weekly-schedule tool — operates on whatever week `CalendarWeekNav` above is
            currently showing, never a separate date picker of its own. Additive-only (see
            CopyWeekModal.jsx): existing slots for this week are never touched. */}
        <div className="hf-tm-bulk-actions">
          {/* Mobile-only — Desktop/Tablet already have their own "Nazad" next to the title
              (.hf-tm-back-btn, in .hf-tm-header-title-block, hidden on Mobile). On Mobile this
              sits in the same row as "Kopiraj prethodnu nedelju" instead of inside the fixed
              MobileHeader, so both read as one action row instead of Nazad floating separately
              above it. */}
          {backTo && (
            <button
              type="button"
              className="hf-tm-bulk-btn hf-tm-bulk-back-btn"
              onClick={() => navigate(backTo)}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              Nazad
            </button>
          )}
          <button
            type="button"
            className="hf-tm-bulk-btn"
            onClick={() => setCopyWeekOpen(true)}
          >
            <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
            Kopiraj prethodnu nedelju
          </button>
        </div>

        <div className="hf-tm-legend">
          <span className="hf-tm-legend-item">
            <i className="hf-tm-swatch hf-tm-swatch-empty" /> Slobodno
          </span>
          <span className="hf-tm-legend-item">
            <i className="hf-tm-swatch hf-tm-swatch-filled" /> Kreiran termin
          </span>
          <span className="hf-tm-legend-item">
            <i className="hf-tm-swatch hf-tm-swatch-hidden" /> Sakriven
          </span>
        </div>
      </div>

      {/* Mobile (<768px): Week View only, reusing the exact same shared grid (and CSS) the
          Calendar screen's mobile view uses (MobileWeekGrid → `.hf-cal-m-week-*`, scheduler.css)
          — same Monday–Saturday structure, time column, day headers, horizontal scroll,
          responsive column widths, current-day/past styling, spacing. Only what renders inside
          a slot (`renderCell`, defined above) is Termini-specific — no day/week switch and no
          single-day view here, matching Calendar's own Day-View-removed mobile behavior. Fixed
          bottom action bar + EmployeePopup as a bottom-sheet modal are otherwise unchanged. */}
      {!isDesktopTablet && (
        <>
          <div className="hf-tm-mobile-view">
            <MobileWeekGrid
              weekDates={weekDates}
              dayLabels={WEEK_DAY_LABELS}
              hours={HOUR_ROWS.filter((row) => row !== "break")}
              breakAfterHour={11}
              renderCell={renderCell}
            />
          </div>

          {selected.size > 0 && (
            <div className="hf-tm-action-bar">
              <span className="hf-tm-action-count">
                Izabrano: {selected.size}
              </span>
              <button
                type="button"
                className="hf-tm-action-btn hf-tm-action-clear"
                onClick={clearSelection}
                aria-label="Poništi izbor"
              >
                <CloseIcon />
              </button>
              {canCreate && (
                <button
                  type="button"
                  className="hf-tm-action-btn hf-tm-action-primary"
                  disabled={saving}
                  onClick={handleCreate}
                >
                  <AddIcon />
                  Kreiraj termine
                </button>
              )}
              {canEditEmployees && (
                <button
                  type="button"
                  className="hf-tm-action-btn hf-tm-action-secondary"
                  disabled={saving}
                  onClick={openEmployeePopup}
                >
                  <GroupsOutlinedIcon />
                  Zaposleni
                </button>
              )}
              {canHide && (
                <button
                  type="button"
                  className="hf-tm-action-btn hf-tm-action-secondary"
                  disabled={saving}
                  onClick={() => handleSetVisibility("hidden")}
                >
                  <VisibilityOffOutlinedIcon />
                  Sakrij
                </button>
              )}
              {canChangeMaxBookings && (
                <button
                  type="button"
                  className="hf-tm-action-btn hf-tm-action-secondary"
                  disabled={saving}
                  onClick={openMaxBookingsPopup}
                >
                  <EventSeatOutlinedIcon />
                  Promeni Broj Vežbača
                </button>
              )}
              {canShow && (
                <button
                  type="button"
                  className="hf-tm-action-btn hf-tm-action-secondary"
                  disabled={saving}
                  onClick={() => handleSetVisibility(null)}
                >
                  <VisibilityOutlinedIcon />
                  Prikaži
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="hf-tm-action-btn hf-tm-action-danger"
                  disabled={saving}
                  onClick={handleDelete}
                >
                  <DeleteOutlineOutlinedIcon />
                  Obriši
                </button>
              )}
            </div>
          )}

          {employeePopup && (
            <EmployeePopup
              employees={employeePopup.employees}
              assignedEmployeeIds={employeePopup.assignedEmployeeIds}
              maxSelected={MAX_EMPLOYEES_PER_TIMESLOT}
              saving={saving}
              onConfirm={handleConfirmEmployeePopup}
              onClose={() => setEmployeePopup(null)}
            />
          )}

          {maxBookingsPopup && (
            <MaxBookingsPopup
              initialValue={maxBookingsPopup.initialValue}
              saving={saving}
              onConfirm={handleConfirmMaxBookings}
              onClose={() => setMaxBookingsPopup(null)}
            />
          )}
        </>
      )}

      {/* Tablet/Desktop (>=768px): master-detail layout — grid on the left,
          a persistent detail panel on the right. The panel always exists;
          it just swaps which content it shows (empty / selected-slots list
          + actions / inline employee picker) instead of ever opening a
          bottom popup. Selection state, handlers and API calls above are
          shared 1:1 with the Mobile branch — only where they're rendered
          differs. */}
      {isDesktopTablet && (
        <div className="hf-tm-body">
          <div className="hf-tm-main">
            <div className="hf-tm-grid-wrapper">
              <div
                className="hf-tm-grid"
                style={{
                  gridTemplateColumns: `64px repeat(${DAYS_PER_WEEK}, minmax(0, 1fr))`,
                }}
              >
                <div className="hf-tm-corner" />
                {weekDates.map((date, index) => (
                  <div key={index} className="hf-tm-day-header">
                    <span className="hf-tm-day-header-label">
                      {WEEK_DAY_LABELS[index]}
                    </span>
                    <span className="hf-tm-day-number">{date.getDate()}</span>
                  </div>
                ))}

                {HOUR_ROWS.map((row) =>
                  row === "break" ? (
                    <div key="break" className="hf-tm-break-row">
                      <span className="hf-tm-break-label">PAUZA</span>
                    </div>
                  ) : (
                    <React.Fragment key={row}>
                      <div className="hf-tm-hour-label">{row}:00</div>
                      {weekDates.map((date, index) => (
                        <React.Fragment key={index}>
                          {renderCell(date, row)}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  )
                )}
              </div>
            </div>
          </div>

          <div className="hf-tm-detail-panel">
            {employeePopup ? (
              <EmployeePopup
                variant="panel"
                employees={employeePopup.employees}
                assignedEmployeeIds={employeePopup.assignedEmployeeIds}
                maxSelected={MAX_EMPLOYEES_PER_TIMESLOT}
                saving={saving}
                onConfirm={handleConfirmEmployeePopup}
                onClose={() => setEmployeePopup(null)}
              />
            ) : maxBookingsPopup ? (
              <MaxBookingsPopup
                variant="panel"
                initialValue={maxBookingsPopup.initialValue}
                saving={saving}
                onConfirm={handleConfirmMaxBookings}
                onClose={() => setMaxBookingsPopup(null)}
              />
            ) : selected.size > 0 ? (
              <div className="hf-tm-detail-selected">
                <div className="hf-tm-detail-header">
                  <div>
                    <h2 className="hf-tm-detail-title">
                      {selected.size} izabrano
                    </h2>
                    <p className="hf-tm-detail-subtitle">
                      Upravljajte izabranim terminima
                    </p>
                  </div>
                  <button
                    type="button"
                    className="hf-tm-detail-clear"
                    onClick={clearSelection}
                    aria-label="Poništi izbor"
                  >
                    <CloseIcon />
                  </button>
                </div>

                <div className="hf-tm-detail-list">
                  {selectedSlotsSorted.map((slot) => (
                    <div key={slot.key} className="hf-tm-detail-chip">
                      <CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />
                      <div className="hf-tm-detail-chip-text">
                        <span className="hf-tm-detail-chip-date">
                          {slot.dateLabel}
                        </span>
                        <span className="hf-tm-detail-chip-time">
                          {slot.timeLabel}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="hf-tm-detail-chip-remove"
                        onClick={() => toggleCell(slot.key)}
                        aria-label="Ukloni termin iz izbora"
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="hf-tm-detail-actions">
                  {canCreate && (
                    <button
                      type="button"
                      className="hf-tm-action-btn hf-tm-action-primary"
                      disabled={saving}
                      onClick={handleCreate}
                    >
                      <AddIcon />
                      Kreiraj termine
                    </button>
                  )}
                  {canEditEmployees && (
                    <button
                      type="button"
                      className="hf-tm-action-btn hf-tm-action-secondary"
                      disabled={saving}
                      onClick={openEmployeePopup}
                    >
                      <GroupsOutlinedIcon />
                      Zaposleni
                    </button>
                  )}
                  {canHide && (
                    <button
                      type="button"
                      className="hf-tm-action-btn hf-tm-action-secondary"
                      disabled={saving}
                      onClick={() => handleSetVisibility("hidden")}
                    >
                      <VisibilityOffOutlinedIcon />
                      Sakrij
                    </button>
                  )}
                  {canChangeMaxBookings && (
                    <button
                      type="button"
                      className="hf-tm-action-btn hf-tm-action-secondary"
                      disabled={saving}
                      onClick={openMaxBookingsPopup}
                    >
                      <EventSeatOutlinedIcon />
                      Promeni Broj Vežbača
                    </button>
                  )}
                  {canShow && (
                    <button
                      type="button"
                      className="hf-tm-action-btn hf-tm-action-secondary"
                      disabled={saving}
                      onClick={() => handleSetVisibility(null)}
                    >
                      <VisibilityOutlinedIcon />
                      Prikaži
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      className="hf-tm-action-btn hf-tm-action-danger"
                      disabled={saving}
                      onClick={handleDelete}
                    >
                      <DeleteOutlineOutlinedIcon />
                      Obriši
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="hf-tm-detail-empty">
                <span className="hf-tm-detail-empty-icon">
                  <ScheduleOutlinedIcon />
                </span>
                <h3 className="hf-tm-detail-empty-title">Izaberite termin</h3>
                <p className="hf-tm-detail-empty-text">
                  Izaberite polje sa leve strane da biste videli i uredili
                  detalje ovde.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {copyWeekOpen && (
        <CopyWeekModal
          open={copyWeekOpen}
          weekDates={weekDates}
          weekLabel={weekLabel}
          timeslotsByStart={timeslotsByStart}
          onClose={() => setCopyWeekOpen(false)}
          onSaved={() => {
            setCopyWeekOpen(false);
            fetchTimeslots();
          }}
        />
      )}
    </div>
  );
};

export default TimeslotsSection;
